import React, {
  JSXElementConstructor,
  ReactElement,
  cloneElement,
  useLayoutEffect,
  useState,
} from 'react';
import { Box, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  sideBarHeader,
  mainContent,
  mainContentMap,
  StyledPageContent,
  iconBox,
  mapIconBox,
  StyledMainTop,
  StyledAppLayout,
} from './style';
import { Header } from '../header/Header';
import { SideBar } from '../sideBar';
import { HeaderNavItemType, NavSection } from 'src/features';
import { useLocation, useNavigate } from 'react-router';
import { ROOT_PATH, SITES_MANAGEMENT_PATH, SITES_MAP_PATH } from 'src/routes/config';
import { useAuthentication } from 'src/features/authentication/context';
import { StreametricLogo } from 'src/assets/icons/StreametricLogo';
import { useCustomer } from 'src/contexts/customerContext/customer.context';
import { useCustomerAssets } from 'src/contexts/assetContext/asset.context';
import { SidebarToggleContextProvider } from './sidebarToggle.context';
import { ExtendedNavItemTypes } from '../extendedHeader/types';
import { CustomerThemeIds } from 'src/contexts/customerContext/types';
// import { useBlockNavigation } from 'src/routes/elements/ReportFlow/BuildReportsElement/blockNavigationContext';
import { confirmNavigationProceed } from 'src/routes/elements/ReportFlow/BuildReportsElement/confirmNavigationProceed';
import { useToggle } from 'src/hooks/useToggle';
import { useResponsive } from 'src/hooks/useResponsive';
import { HamburgerIcon } from 'src/assets/icons/HamburgerIcon';
import { DoubleLeftArrowIcon } from 'src/assets/icons/common/DoubleLeftArrowIcon';
import { ReportNotificationHandler } from './ReportNotificationHandler';

const MemoizedSideBar = React.memo(SideBar);

type AppLayoutProps = {
  children: React.ReactNode;
  isTitleEditable?: boolean;
  headerTitle?: string;
  headerNavItems: HeaderNavItemType[];
  onNavigateBackClick?: () => void;
  extendedHeaderTitle?: string;
  isSiteHeader?: boolean;
  isSuperAdmin?: boolean;
  onTitleChange?: (title: string) => void;
  isCustomerAccountPage?: boolean;
  extendedNavItems?: ExtendedNavItemTypes[];
  activeExtendedNavItem?: string;
  isExtendedHeader?: boolean;
  defaultExtendedHeaderTitle?: string;
  hideSiteHeader?: boolean;
};

export const AppLayout = ({
  children,
  headerTitle,
  headerNavItems,
  isSuperAdmin,
  isTitleEditable,
  extendedHeaderTitle,
  defaultExtendedHeaderTitle,
  activeExtendedNavItem,
  isCustomerAccountPage,
  isSiteHeader = false,
  extendedNavItems,
  onTitleChange,
  onNavigateBackClick,
  isExtendedHeader = false,
  hideSiteHeader,
}: AppLayoutProps) => {
  const theme = useTheme();

  const whiteColor = theme.palette.common.white;
  const [logoUrl, setLogoUrl] = useState<string>('');

  const doubleLeftArrowIconColor = isSuperAdmin ? whiteColor : theme.palette.text.secondary;

  const isMobile = useResponsive('down', 'md');
  const { state, onToggle } = useToggle();
  const navigate = useNavigate();
  const {
    getCurrentRole,
    getAllCustomerIds,
    customerId: { value: customerId },
  } = useAuthentication();
  const { uniqueIds } = { ...getAllCustomerIds() };
  const location = useLocation();
  const validCustomerId = customerId ?? '';

  const searchParams = new URLSearchParams(location.search);
  const isFullscreen = searchParams.get('fullscreen') === 'true';

  const { themeIds, loading, customerName } = useCustomer(validCustomerId);

  const { customerAssets, loadingAssets } = useCustomerAssets(
    themeIds?.value.activeThemeId,
    validCustomerId
  );

  // const blockNavigation = useBlockNavigation();
  const handleLogoClick = () => {
    let shouldBlockNavigation = false;
    // if (blockNavigation && blockNavigation[0]) shouldBlockNavigation = true;
    if (shouldBlockNavigation) {
      const confirmed = confirmNavigationProceed();
      if (!confirmed) return false;
    }
    if (!customerId || getCurrentRole(null, null) === 'SUPER_ADMIN') {
      navigate(ROOT_PATH);
      return;
    }

    navigate(SITES_MANAGEMENT_PATH + '?customerId=' + customerId);
  };
  const handleCloseSideBar = () => {
    if (state) onToggle();
  };
  useLayoutEffect(() => {
    if (customerAssets?.logo) {
      setLogoUrl(customerAssets?.logo);
    }
  }, [customerAssets?.logo, location.pathname]);
  const greyColorBold = theme.palette.text.secondary;
  const isSiteMap = location.pathname === SITES_MAP_PATH;
  const hederIconStyles = isSiteMap ? mapIconBox : iconBox;
  const mainContentStyles = isSiteMap ? { ...mainContent, ...mainContentMap } : mainContent;

  const showHeaderComponent = (isSiteMap && !isMobile) || !isSiteMap;
  const cloneChildren = isSiteMap && isMobile;

  const renderStreametricLogo = () => <StreametricLogo />;
  const renderCustomLogo = (logo: string) => (
    <Box
      component="img"
      rel="preload"
      src={logoUrl}
      width={200}
      height={100}
      sx={{ border: 'none' }}
    />
  );

  const renderLogo = (
    loading: boolean,
    themeIds: CustomerThemeIds | undefined,
    customerId: string,
    logoUrl: string | undefined,
    path: string
  ) => {
    if (path === '/') {
      return renderStreametricLogo();
    }
    if (loading && !themeIds?.activeThemeId && !logoUrl) {
      return <></>;
    }
    if (!loading && customerId && logoUrl) {
      return renderCustomLogo(logoUrl);
    } else if (!loading && !themeIds?.activeThemeId && customerId && !logoUrl) {
      return renderStreametricLogo();
    }
  };

  const SiteHeader = ({ hideSiteHeader }: { hideSiteHeader?: boolean }) => {
    let currentRole;
    if (getCurrentRole) {
      currentRole = getCurrentRole(null, null);
    }

    if (!hideSiteHeader) {
      return (
        <SidebarToggleContextProvider toggle={state} onToggle={onToggle}>
          <StyledMainTop isTransparent={isMobile && isSiteMap} theme={theme} isMobile={isMobile}>
            {isMobile && !isExtendedHeader && !isSiteHeader ? (
              <Box sx={hederIconStyles} onClick={onToggle} data-sm="side-bar-open">
                <HamburgerIcon fill={greyColorBold} />
              </Box>
            ) : null}
            <Header
              extendedHeaderTitle={extendedHeaderTitle}
              defaultExtendedHeaderTitle={defaultExtendedHeaderTitle}
              isTitleEditable={isTitleEditable}
              activeExtendedNavItem={activeExtendedNavItem}
              onTitleChange={onTitleChange}
              isSiteHeader={isSiteHeader}
              isFullscreen={isFullscreen}
              onNavigateBackClick={onNavigateBackClick}
              extendedNavItems={extendedNavItems}
              isExtendedHeader={isExtendedHeader}
              title={headerTitle}
              isTransparent={isMobile && isSiteMap}
              showButton={currentRole !== 'SUPER_ADMIN' && uniqueIds ? uniqueIds.length > 1 : false}
            />
          </StyledMainTop>
        </SidebarToggleContextProvider>
      );
    }

    return (
      <SidebarToggleContextProvider toggle={state} onToggle={onToggle}>
        <Box
          sx={{
            position: 'absolute',
            top: '26px',
            left: 0,
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            px: 2,
            zIndex: 10,
            backgroundColor: 'transparent',
          }}
        >
          <Box sx={{ cursor: 'pointer' }} onClick={onToggle} data-sm="side-bar-open">
            <HamburgerIcon fill={greyColorBold} />
          </Box>
        </Box>
      </SidebarToggleContextProvider>
    );
  };

  if (loadingAssets) return <></>;

  return (
    <StyledAppLayout>
      {!isFullscreen ? (
        <MemoizedSideBar
          isOpen={state}
          isMobile={isMobile}
          isSuperAdmin={isSuperAdmin}
          isCustomerAccountPage={isCustomerAccountPage}
        >
          <Box sx={sideBarHeader}>
            <Box onClick={handleLogoClick} sx={{ cursor: 'pointer' }}>
              {renderLogo(
                loadingAssets,
                themeIds?.value,
                validCustomerId,
                logoUrl,
                location.pathname
              )}
            </Box>
            {isMobile ? (
              <Stack
                flexDirection={'column'}
                justifyContent={'center'}
                onClick={onToggle}
                data-sm="side-bar-close"
              >
                <DoubleLeftArrowIcon height={17} width={20} fill={doubleLeftArrowIconColor} />
              </Stack>
            ) : null}
          </Box>
          <NavSection
            navHeaderItems={headerNavItems}
            handleCloseSideBar={handleCloseSideBar}
            isSuperAdmin={isSuperAdmin}
            isCustomerAccountPage={isCustomerAccountPage}
            customerLoading={loading}
            customerName={customerName}
          />
        </MemoizedSideBar>
      ) : null}

      <Box
        component="main"
        sx={{ ...mainContentStyles, backgroundColor: theme.palette.background.paper }}
        onClick={handleCloseSideBar}
      >
        {showHeaderComponent && <SiteHeader hideSiteHeader={hideSiteHeader} />}
        <ReportNotificationHandler customerId={customerId!!} />
        <StyledPageContent
          theme={theme}
          isMobile={isMobile}
          component="section"
          noPadding={isSiteMap || isFullscreen}
        >
          {cloneChildren
            ? cloneElement(children as ReactElement<any, string | JSXElementConstructor<any>>, {
                SiteHeader: SiteHeader,
              })
            : children}
        </StyledPageContent>
      </Box>
    </StyledAppLayout>
  );
};
